import { useState, useEffect, useRef } from 'react';
import { foundationsSessionDuration } from '@/config/courseDurations';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import type { LucideIcon } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { useCoursePricing } from '@/hooks/useCoursePricing';
import { useLocation } from '@/contexts/LocationContext';
import {
  DollarSign,
  ChevronRight,
  ChevronDown,
  Check,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type IconType = LucideIcon;

export type Year3FoundationsPageData = {
  route: string;
  breadcrumb: string[];
  pill: string;
  h1: string;
  subtitle: string;
  quickFacts: { icon: IconType; text: string }[];
  courseDetailsHeading: string;
  pricingHeading: string;
  relatedHeading: string;
  tabs: {
    id: string;
    icon: IconType;
    name: string;
    sub: string;
    items: {
      label: string;
      name: string;
      heading: string;
      para: string;
      bullets: string[];
    }[];
  }[];
  inclusions: {
    icon: IconType;
    name: string;
    desc: string;
    images?: string[];
  }[];
  faq: { q: string; a: string }[];
  related: { tag: string; name: string; desc: string; href: string }[];
  seo: {
    title: string;
    description: string;
    schemaCourseName: string;
    schemaDescription: string;
  };
  enrolCtaLabel?: string;
  /** Year level used to query DB pricing (defaults to 3). */
  yearLevel?: number;
  /** State code used to query DB pricing (defaults to 'NSW'). */
  stateCode?: 'NSW' | 'VIC' | 'WA';
  /** When true, never fall back to a hardcoded price; hide price until the DB row loads. */
  requireDbPrice?: boolean;
  /** ISO 8601 per-session workload for Course JSON-LD (e.g. PT1H). */
  courseWorkload?: string;
  /** Emit og/twitter tags from the page title and description. */
  socialMeta?: boolean;
  /** Optional BreadcrumbList JSON-LD items. */
  breadcrumbSchema?: { name: string; url: string }[];
};

// ─────────────────────────────────────────────────────────────────────────────
// LMS image carousel - auto-cycles through screenshots
// ─────────────────────────────────────────────────────────────────────────────
const LmsImageCarousel = ({
  images,
  alt,
  borderColor,
}: {
  images: string[];
  alt: string;
  borderColor: string;
}) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (images.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % images.length), 4000);
    return () => clearInterval(t);
  }, [images.length]);
  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: 220,
        background: '#f1f5f9',
        borderBottom: `0.5px solid ${borderColor}`,
        overflow: 'hidden',
      }}
    >
      {images.map((src, i) => (
        <img
          key={src}
          src={src}
          alt={alt}
          loading="lazy"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'top',
            opacity: i === idx ? 1 : 0,
            transition: 'opacity 700ms ease-in-out',
          }}
        />
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Design tokens
// ─────────────────────────────────────────────────────────────────────────────

const C = {
  bgSecondary: '#FAFAF8',
  text: '#1a1a1a',
  textMuted: '#6B6B6B',
  border: '#E5E5E5',
  orange: '#f44321',
  orangeDark: '#993C1D',
  orangeTint: '#FAECE7',
  green: '#1D9E75',
  amber: '#FAC775',
  radiusMd: '8px',
  radiusLg: '12px',
};

const BOOK_HREF = '/book-assessment';

const SectionTag = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      fontSize: 11,
      fontWeight: 500,
      color: C.orangeDark,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      marginBottom: 12,
    }}
  >
    {children}
  </div>
);

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <h2 style={{ fontSize: 19, fontWeight: 500, color: C.text, margin: 0, marginBottom: 20 }}>
    {children}
  </h2>
);

const NAV_LINKS = [
  { id: 'overview', label: 'Overview' },
  { id: 'course-details', label: 'Course details' },
  { id: 'whats-included', label: "What's included" },
  { id: 'pricing', label: 'Pricing' },
  { id: 'faq', label: 'FAQ' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Template
// ─────────────────────────────────────────────────────────────────────────────

export default function Year3FoundationsTemplate({ page }: { page: Year3FoundationsPageData }) {
  const [, setActiveSection] = useState('overview');
  const [activeTab, setActiveTab] = useState(0);
  const [activeItem, setActiveItem] = useState(0);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [pricingChoice, setPricingChoice] = useState<'term' | 'session'>('term');
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  // Pricing from DB - falls back to centre-specific price when a matching centre is selected
  const { selectedState, selectedCenterId } = useLocation();
  const stateCode = page.stateCode ?? 'NSW';
  const { getPricingForYearRange } = useCoursePricing(
    stateCode,
    'foundations',
    selectedState === stateCode ? selectedCenterId || undefined : undefined,
  );
  const yl = page.yearLevel ?? 3;
  const dbPricing = getPricingForYearRange(yl, yl);
  const strict = !!page.requireDbPrice;
  const hasPrice = !!dbPricing || !strict;
  const pricePerSession = dbPricing ? Number(dbPricing.price_per_session) : 39;
  const totalSessions = dbPricing?.total_sessions ?? 10;
  const priceLabel = hasPrice
    ? `$${pricePerSession % 1 === 0 ? pricePerSession.toFixed(0) : pricePerSession.toFixed(2)}`
    : '';

  const duration = foundationsSessionDuration(yl);
  const fillDuration = (s: string) =>
    s.split('{DURATION_SHORT}').join(duration.short).split('{DURATION_LENGTH}').join(duration.length);
  const courseWorkload = page.courseWorkload ?? duration.iso;
  const quickFacts = page.quickFacts
    .map((f) => (f.icon === DollarSign ? { ...f, text: hasPrice ? `${priceLabel} / session` : '' } : { ...f, text: fillDuration(f.text) }))
    .filter((f) => f.text);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActiveSection(visible.target.id);
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: [0, 0.25, 0.5, 1] },
    );
    NAV_LINKS.forEach((l) => {
      const el = document.getElementById(l.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setActiveItem(0);
  }, [activeTab]);

  const tab = page.tabs[activeTab];
  const item = tab.items[activeItem];

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: page.seo.schemaCourseName,
    description: page.seo.schemaDescription,
    provider: { '@type': 'Organization', name: 'Schoolola Tutoring', url: 'https://schoolola.com.au' },
    ...(hasPrice
      ? {
          offers: {
            '@type': 'Offer',
            price: String(pricePerSession),
            priceCurrency: 'AUD',
            priceSpecification: 'per session',
          },
        }
      : {}),
    ...(courseWorkload
      ? {
          hasCourseInstance: {
            '@type': 'CourseInstance',
            courseMode: 'blended',
            courseWorkload,
          },
        }
      : {}),
    url: `https://schoolola.com.au${page.route}`,
  };

  // Book pack copy, driven by course_pricing.books_cost for this state/year.
  const booksCost = dbPricing ? Number(dbPricing.books_cost ?? 0) : null;
  const booksText =
    booksCost == null
      ? ''
      : booksCost > 0
        ? `The book pack is $${booksCost % 1 === 0 ? booksCost.toFixed(0) : booksCost.toFixed(2)}, charged separately.`
        : 'There is no separate book pack charge.';

  // Strict (config-driven) pages resolve prices in their own copy, so the
  // legacy "$N" substitution is skipped to avoid overwriting book prices.
  const replacePriceText = (s: string) =>
    (strict ? s : s.replace(/\$\d+(?:\.\d+)?/g, priceLabel))
      .split('{PRICE}')
      .join(priceLabel || 'the session price')
      .split('{BOOKS}')
      .join(booksText)
      .split('{DURATION_LENGTH}')
      .join(duration.length)
      .split('{DURATION_SHORT}')
      .join(duration.short)
      .replace(/\s+$/, '');
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: page.faq.map((f) => ({
      '@type': 'Question',
      name: replacePriceText(f.q),
      acceptedAnswer: { '@type': 'Answer', text: replacePriceText(f.a) },
    })),
  };

  const breadcrumbSchema = page.breadcrumbSchema
    ? {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: page.breadcrumbSchema.map((b, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: b.name,
          item: b.url,
        })),
      }
    : null;

  const pageUrl = `https://schoolola.com.au${page.route}`;

  return (
    <>
      <Helmet>
        <title>{page.seo.title}</title>
        <meta name="description" content={page.seo.description} />
        {page.socialMeta && <meta property="og:title" content={page.seo.title} />}
        {page.socialMeta && <meta property="og:description" content={page.seo.description} />}
        {page.socialMeta && <meta property="og:url" content={pageUrl} />}
        {page.socialMeta && <meta property="og:type" content="website" />}
        {page.socialMeta && <meta name="twitter:card" content="summary_large_image" />}
        {page.socialMeta && <meta name="twitter:title" content={page.seo.title} />}
        {page.socialMeta && <meta name="twitter:description" content={page.seo.description} />}
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        {breadcrumbSchema && (
          <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        )}
      </Helmet>

      <Header />

      <main style={{ color: C.text, fontSize: 14, fontWeight: 400 }}>
        {/* ─── HERO ─── */}
        <section id="overview" style={{ background: C.bgSecondary, padding: '48px 24px' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 16 }}>
              {page.breadcrumb.map((b, i) => (
                <span key={i}>
                  {i > 0 && <span style={{ margin: '0 6px' }}>›</span>}
                  {b}
                </span>
              ))}
            </div>
            <span
              style={{
                display: 'inline-block',
                background: '#f44321',
                color: '#fff',
                fontSize: 11,
                fontWeight: 500,
                padding: '4px 12px',
                borderRadius: 999,
                marginBottom: 16,
              }}
            >
              {page.pill}
            </span>
            <h1 style={{ fontSize: 32, fontWeight: 500, margin: 0, marginBottom: 12, color: C.text }}>
              {page.h1}
            </h1>
            <p style={{ fontSize: 14, color: C.textMuted, margin: 0, marginBottom: 24, maxWidth: 720 }}>
              {page.subtitle}
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24 }}>
              {quickFacts.map((f, i) => {
                const Icon = f.icon;
                return (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      background: '#fff',
                      border: `0.5px solid ${C.border}`,
                      borderRadius: C.radiusMd,
                      padding: '8px 12px',
                      fontSize: 12,
                      color: C.text,
                    }}
                  >
                    <Icon size={14} color={C.orange} />
                    {f.text}
                  </div>
                );
              })}
            </div>
            <Button asChild>
              <Link to={BOOK_HREF}>Book a free assessment</Link>
            </Button>
          </div>
        </section>

        {/* ─── COURSE DETAILS ─── */}
        <section id="course-details" style={{ padding: '56px 24px', background: '#fff' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <SectionTag>COURSE DETAILS</SectionTag>
            <SectionHeading>{page.courseDetailsHeading}</SectionHeading>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
              {page.tabs.map((t, i) => {
                const active = i === activeTab;
                const disabled = i === 1;
                const TabIcon = t.icon;
                return (
                  <button
                    key={t.id}
                    onClick={() => !disabled && setActiveTab(i)}
                    disabled={disabled}
                    style={{
                      background: disabled ? '#f5f5f5' : '#fff',
                      border: active ? `1.5px solid ${C.orange}` : `0.5px solid ${C.border}`,
                      borderRadius: C.radiusLg,
                      padding: '16px 18px',
                      textAlign: 'left',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      opacity: disabled ? 0.6 : 1,
                      position: 'relative',
                    }}
                  >
                    <TabIcon size={20} color={disabled ? C.textMuted : C.orange} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 15, fontWeight: 500, color: C.text }}>{t.name}</div>
                      <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>{t.sub}</div>
                    </div>
                    {disabled && (
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                          background: C.orange,
                          color: '#fff',
                          padding: '4px 8px',
                          borderRadius: 999,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Coming soon
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div
              style={{
                display: 'flex',
                border: `0.5px solid ${C.border}`,
                borderRadius: C.radiusLg,
                overflow: 'hidden',
                background: '#fff',
                opacity: activeTab === 1 ? 0.5 : 1,
                pointerEvents: activeTab === 1 ? 'none' : 'auto',
                filter: activeTab === 1 ? 'grayscale(0.6)' : 'none',
              }}
            >
              <div style={{ width: 180, borderRight: `0.5px solid ${C.border}`, flexShrink: 0 }}>
                {tab.items.map((it, i) => {
                  const active = i === activeItem;
                  return (
                    <button
                      key={i}
                      onClick={() => setActiveItem(i)}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        background: active ? C.orangeTint : 'transparent',
                        border: 'none',
                        borderLeft: active ? `3px solid ${C.orange}` : '3px solid transparent',
                        padding: '14px 16px',
                        cursor: 'pointer',
                      }}
                    >
                      <div style={{ fontSize: 10, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        {it.label}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: C.text, marginTop: 2 }}>
                        {it.name}
                      </div>
                    </button>
                  );
                })}
              </div>
              <div style={{ flex: 1, padding: 16 }}>
                <h3 style={{ fontSize: 16, fontWeight: 500, margin: 0, marginBottom: 10, color: C.text }}>
                  {item.heading}
                </h3>
                <p style={{ fontSize: 13, color: C.textMuted, margin: 0, marginBottom: 14 }}>
                  {fillDuration(item.para)}
                </p>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {item.bullets.map((b, i) => (
                    <li
                      key={i}
                      style={{
                        display: 'flex',
                        gap: 8,
                        alignItems: 'flex-start',
                        padding: '6px 0',
                        fontSize: 13,
                        color: C.textMuted,
                      }}
                    >
                      <ChevronRight size={14} color={C.orange} style={{ flexShrink: 0, marginTop: 3 }} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ─── WHAT'S INCLUDED ─── */}
        <section id="whats-included" style={{ background: C.bgSecondary, padding: '56px 24px' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <SectionTag>WHAT'S INCLUDED</SectionTag>
            <SectionHeading>Term course inclusions</SectionHeading>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
              {page.inclusions.map((inc, i) => {
                const hasImages = !!inc.images && inc.images.length > 0;
                const IncIcon = inc.icon;
                return (
                  <div
                    key={i}
                    style={{
                      background: '#fff',
                      border: `0.5px solid ${C.border}`,
                      borderRadius: C.radiusLg,
                      padding: hasImages ? 0 : 14,
                      gridColumn: hasImages ? 'span 2' : undefined,
                      overflow: 'hidden',
                    }}
                  >
                    {hasImages && (
                      <LmsImageCarousel images={inc.images!} alt={`${inc.name} preview`} borderColor={C.border} />
                    )}
                    <div style={{ padding: hasImages ? 14 : 0 }}>
                      <div
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          background: C.orangeTint,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginBottom: 10,
                        }}
                      >
                        <IncIcon size={14} color={C.orange} />
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: C.text, marginBottom: 4 }}>
                        {inc.name}
                      </div>
                      <div style={{ fontSize: 12, color: C.textMuted, lineHeight: 1.4 }}>
                        {inc.desc}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ─── PRICING ─── */}
        <section id="pricing" style={{ padding: '56px 24px', background: '#fff' }}>
          <div style={{ maxWidth: 720, margin: '0 auto' }}>
            <SectionTag>PRICING</SectionTag>
            <SectionHeading>{page.pricingHeading}</SectionHeading>

            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 24 }}>
              <span style={{ fontSize: 38, fontWeight: 500, color: C.text }}>{priceLabel}</span>
              <span style={{ fontSize: 15, color: C.textMuted }}>/ session</span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <button
                onClick={() => setPricingChoice('term')}
                style={{
                  textAlign: 'left',
                  background: '#fff',
                  border: pricingChoice === 'term' ? `1.5px solid ${C.orange}` : `0.5px solid ${C.border}`,
                  borderRadius: C.radiusLg,
                  padding: 18,
                  cursor: 'pointer',
                }}
              >
                <span
                  style={{
                    display: 'inline-block',
                    background: C.orangeTint,
                    color: C.orangeDark,
                    fontSize: 10,
                    fontWeight: 500,
                    padding: '3px 10px',
                    borderRadius: 999,
                    marginBottom: 10,
                  }}
                >
                  Recommended
                </span>
                <div style={{ fontSize: 15, fontWeight: 500, color: C.text }}>Term enrolment</div>
                <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4, marginBottom: 12 }}>
                  {totalSessions} sessions per term · reserve your spot for the full term
                </div>
                {[
                  'End-of-term progress report',
                  'Guaranteed place for the term',
                  'Payment plan available',
                ].map((f) => (
                  <div key={f} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 0', fontSize: 12, color: C.text }}>
                    <Check size={14} color={C.green} />
                    {f}
                  </div>
                ))}
              </button>

              <button
                onClick={() => setPricingChoice('session')}
                style={{
                  textAlign: 'left',
                  background: '#fff',
                  border: pricingChoice === 'session' ? `1.5px solid ${C.orange}` : `0.5px solid ${C.border}`,
                  borderRadius: C.radiusLg,
                  padding: 18,
                  cursor: 'pointer',
                }}
              >
                <span style={{ display: 'inline-block', fontSize: 10, padding: '3px 10px', marginBottom: 10, visibility: 'hidden' }}>
                  spacer
                </span>
                <div style={{ fontSize: 15, fontWeight: 500, color: C.text }}>Pay per session</div>
                <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4, marginBottom: 12 }}>
                  Book and pay session by session · no term commitment
                </div>
                {[
                  'Flexible - stop or pause anytime',
                  `Same ${priceLabel} per session`,
                  'Subject to availability',
                ].map((f) => (
                  <div key={f} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 0', fontSize: 12, color: C.text }}>
                    <Check size={14} color={C.green} />
                    {f}
                  </div>
                ))}
              </button>
            </div>

            <Button
              asChild
              size="lg"
              className="w-full mb-3 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-white font-medium rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] border-none"
            >
              <Link to={BOOK_HREF}>
                {pricingChoice === 'term' ? page.enrolCtaLabel ?? 'Enrol for Term 3' : 'Book a session'}
              </Link>
            </Button>
            <p style={{ fontSize: 12, color: C.textMuted, margin: 0, textAlign: 'center' }}>
              Start with a free assessment · we'll confirm your child's level before the first session
            </p>
          </div>
        </section>

        {/* ─── FAQ ─── */}
        <section id="faq" style={{ background: C.bgSecondary, padding: '56px 24px' }}>
          <div style={{ maxWidth: 800, margin: '0 auto' }}>
            <SectionTag>FAQ</SectionTag>
            <SectionHeading>Common questions</SectionHeading>
            <div
              style={{
                background: '#fff',
                border: `0.5px solid ${C.border}`,
                borderRadius: C.radiusLg,
                overflow: 'hidden',
              }}
            >
              {page.faq.map((f, i) => {
                const open = openFaq === i;
                const replacePrice = replacePriceText;
                return (
                  <div key={i} style={{ borderTop: i === 0 ? 'none' : `0.5px solid ${C.border}` }}>
                    <button
                      onClick={() => setOpenFaq(open ? null : i)}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        background: 'transparent',
                        border: 'none',
                        padding: '16px 18px',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 12,
                        fontSize: 14,
                        fontWeight: 500,
                        color: C.text,
                      }}
                    >
                      <span>{replacePrice(f.q)}</span>
                      <ChevronDown
                        size={16}
                        color={C.textMuted}
                        style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}
                      />
                    </button>
                    {open && (
                      <div style={{ padding: '0 18px 16px', fontSize: 13, color: C.textMuted, lineHeight: 1.55 }}>
                        {replacePrice(f.a)}
                      </div>
                    )}
                  </div>
                );
              })}

            </div>
          </div>
        </section>

        {/* ─── RELATED COURSES ─── */}
        <section style={{ padding: '56px 24px', background: '#fff' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <SectionTag>RELATED COURSES</SectionTag>
            <SectionHeading>{page.relatedHeading}</SectionHeading>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {page.related.map((r) => (
                <Link
                  key={r.href}
                  to={r.href}
                  style={{
                    background: '#fff',
                    border: `0.5px solid ${C.border}`,
                    borderRadius: C.radiusLg,
                    padding: 18,
                    textDecoration: 'none',
                    color: 'inherit',
                  }}
                >
                  <div style={{ fontSize: 11, color: C.orange, fontWeight: 500, marginBottom: 8 }}>
                    {r.tag}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: C.text, marginBottom: 4 }}>
                    {r.name}
                  </div>
                  <div style={{ fontSize: 12, color: C.textMuted }}>{r.desc}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ─── FOOTER CTA ─── */}
        <section style={{ background: C.orange, padding: '40px 24px' }}>
          <div style={{ maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>
            <h2 style={{ fontSize: 18, fontWeight: 500, color: '#fff', margin: 0, marginBottom: 8 }}>
              Start with a free assessment
            </h2>
            <p style={{ fontSize: 13, color: C.amber, margin: 0, marginBottom: 18 }}>
              We'll confirm your child is at the right level before their first term starts.
            </p>
            <Button asChild size="lg" variant="secondary">
              <Link to={BOOK_HREF}>Book now - it's free</Link>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
